import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import preloadService from '../services/preloadService';

const AuthContext = createContext();

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(() => {
		try {
			const saved = localStorage.getItem('user');
			return saved ? JSON.parse(saved) : null;
		} catch {
			return null;
		}
	});
	const [token, setToken] = useState(localStorage.getItem('authToken'));
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// Clear error when component unmounts
	useEffect(() => {
		return () => {
			setError(null); // Clear error on unmount
		};
	}, []);

	// Check if user is authenticated on app load
	useEffect(() => {
		const checkAuth = async () => {
			if (token) {
				try {
					const profile = await authAPI.getProfile();
					const profileData = profile?.data || profile?.user || profile || null;
					if (profileData) {
						setUser(profileData);
						localStorage.setItem('user', JSON.stringify(profileData));
					}
				} catch (err) {
					// If 401 (Unauthorized), the token is invalid - clear it silently
					if (err.response?.status === 401) {
						localStorage.removeItem('authToken');
						localStorage.removeItem('user');
						setToken(null);
						setUser(null);
						// Don't log this - it's expected when token is expired
					} else {
						// For other errors, silently fail but keep session
						// This avoids redirect loops if the profile endpoint differs
					}
				}
			}
			setLoading(false);
		};

		checkAuth();
	}, [token]);

	// Login function
	const login = async (credentials) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.login(credentials);
			
			// Check if the response indicates success (Laravel format)
			if (response.status === true || response.success === true) {
				const { token: authToken, user: userData } = response.data || response;
				
				// Check if user is an expert and account is not approved
				if (userData?.role?.toLowerCase() === 'expert') {
					// Check if expert account status is approved
					if (userData.status && userData.status !== 'approved') {
						throw new Error('Account review pending. We will notify you once your account is approved.');
					}
				}
				
				localStorage.setItem('authToken', authToken);
				localStorage.setItem('user', JSON.stringify(userData));
				setToken(authToken);
				setUser(userData);
				
				// Trigger user-specific data preloading after successful login
				setTimeout(async () => {
					try {
						await preloadService.preloadUserData();
					} catch (error) {
						console.warn('[Auth] User data preload failed:', error);
					}
				}, 1000); // Small delay to ensure token is set
				
				return { success: true, message: response.message, user: userData };
			} else {
				// If status is false, show the specific message from response
				throw new Error(response.message || 'Login failed');
			}
		} catch (err) {
			let errorMessage = 'Login failed';
			
			// Handle 422 validation errors
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					// Extract validation errors from the response
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else {
					errorMessage = err.response.data?.message || 'Validation failed. Please check your input.';
				}
			} else if (err.response?.status === 401) {
				// Handle authentication errors
				errorMessage = err.response.data?.message || 'Invalid credentials';
			} else if (err.response?.data) {
				errorMessage = err.response.data.message || err.response.data.error || 'Login failed';
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Register function
	const register = async (userData) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.register(userData);
			
		// Check if the response indicates success (Laravel format)
		if (response.status === true || response.success === true) {
			return { 
				success: true, 
				status: true,
				message: response.message,
				data: response.data 
			};
		} else {
			// If status is false, return the response with errors object so it can be handled properly
			return {
				success: false,
				status: false,
				message: response.message || 'Registration failed',
				errors: response.errors || null
			};
		}
	} catch (err) {
		let errorMessage = 'Registration failed';
		let errors = null;
		
		// Handle 422 validation errors
		if (err.response?.status === 422) {
			if (err.response.data?.errors) {
				// Preserve the errors object
				errors = err.response.data.errors;
				// Extract validation errors from the response for the message
				const validationErrors = err.response.data.errors;
				if (typeof validationErrors === 'object') {
					errorMessage = Object.values(validationErrors).flat().join(', ');
				} else {
					errorMessage = validationErrors;
				}
			} else if (err.response.data?.message) {
				errorMessage = err.response.data.message;
			} else {
				errorMessage = 'Validation failed. Please check your input.';
			}
		} 
		// Handle other API errors
		else if (err.response?.data) {
			// Check if the API response has a status field that's false
			if (err.response.data.status === false) {
				errorMessage = err.response.data.message || 'Registration failed';
				errors = err.response.data.errors || null;
			} else {
				errorMessage = err.response.data.message || err.response.data.error || 'Registration failed';
			}
		} 
		// Handle network or other errors
		else if (err.message) {
			errorMessage = err.message;
		}
		
		setError(errorMessage);
		return { 
			success: false, 
			status: false,
			message: errorMessage,
			errors: errors
		};
		} finally {
			setLoading(false);
		}
	};

	// Verify Email or Phone
	const verifyEmailOrPhone = async (otp, email) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.verifyEmailOrPhone(otp, email);
			
			if (response.status === true || response.success === true) {
				return { success: true, status: true, message: response.message };
			} else {
				throw new Error(response.message || 'Verification failed');
			}
		} catch (err) {
			let errorMessage = 'Verification failed';
			
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else {
					errorMessage = 'Validation failed. Please check your input.';
				}
			} else if (err.response?.data) {
				if (err.response.data.status === false) {
					errorMessage = err.response.data.message || 'Verification failed';
				} else {
					errorMessage = err.response.data.message || err.response.data.error || 'Verification failed';
				}
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			// Handle specific "Invalid OTP" error with better messaging
			if (errorMessage.toLowerCase().includes('invalid otp')) {
				errorMessage = 'Please enter a valid OTP';
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Resend Verification Code
	const resendVerification = async (userId) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.resendVerification(userId);
			
			if (response.status === true || response.success === true) {
				return { success: true, status: true, message: response.message };
			} else {
				throw new Error(response.message || 'Failed to resend verification code');
			}
		} catch (err) {
			let errorMessage = 'Failed to resend verification code';
			
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else {
					errorMessage = 'Validation failed. Please try again.';
				}
			} else if (err.response?.data) {
				if (err.response.data.status === false) {
					errorMessage = err.response.data.message || 'Failed to resend verification code';
				} else {
					errorMessage = err.response.data.message || err.response.data.error || 'Failed to resend verification code';
				}
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Update Farm Details
	const updateFarmDetails = async (farmData) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.updateFarmDetails(farmData);
			
			if (response.status === true || response.success === true) {
				return { success: true, status: true, message: response.message };
			} else {
				throw new Error(response.message || 'Farm details update failed');
			}
		} catch (err) {
			let errorMessage = 'Farm details registration failed';
			
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else {
					errorMessage = 'Validation failed. Please check your input.';
				}
			} else if (err.response?.data) {
				if (err.response.data.status === false) {
					errorMessage = err.response.data.message || 'Farm details registration failed';
				} else {
					errorMessage = err.response.data.message || err.response.data.error || 'Farm details registration failed';
				}
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Update Expert Details
	const updateExpertDetails = async (expertData) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.updateExpertDetails(expertData);
			
			if (response.status === true || response.success === true) {
				return { success: true, status: true, message: response.message };
			} else {
				throw new Error(response.message || 'Expert details update failed');
			}
		} catch (err) {
			let errorMessage = 'Expert details registration failed';
			
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else {
					errorMessage = 'Validation failed. Please check your input.';
				}
			} else if (err.response?.data) {
				if (err.response.data.status === false) {
					errorMessage = err.response.data.message || 'Expert details registration failed';
				} else {
					errorMessage = err.response.data.message || err.response.data.error || 'Expert details registration failed';
				}
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Get Produce List
	const getProduce = async () => {
		try {
			setError(null);
			const response = await authAPI.getProduce();
			return { success: true, data: response };
		} catch (err) {
			let errorMessage = 'Failed to fetch produce list';
			if (err.response?.data) {
				errorMessage = err.response.data.message || err.response.data.error || 'Failed to fetch produce list';
			} else if (err.message) {
				errorMessage = err.message;
			}
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Store Farm Produce
	const storeFarmProduce = async (produceIds, userId) => {
		try {
			setError(null);
			setLoading(true);
			const response = await authAPI.storeFarmProduce(produceIds, userId);
			
			if (response.status === true || response.success === true) {
				return { success: true, status: true, message: response.message };
			} else {
				throw new Error(response.message || 'Failed to store farm produce');
			}
		} catch (err) {
			let errorMessage = 'Failed to store farm produce';
			
			if (err.response?.status === 422) {
				if (err.response.data?.errors) {
					const validationErrors = err.response.data.errors;
					if (typeof validationErrors === 'object') {
						errorMessage = Object.values(validationErrors).flat().join(', ');
					} else {
						errorMessage = validationErrors;
					}
				} else if (err.response.data?.message) {
					errorMessage = err.response.data.message;
				} else {
					errorMessage = 'Validation failed. Please check your input.';
				}
			} else if (err.response?.data) {
				if (err.response.data.status === false) {
					errorMessage = err.response.data.message || 'Failed to store farm produce';
				} else {
					errorMessage = err.response.data.message || err.response.data.error || 'Failed to store farm produce';
				}
			} else if (err.message) {
				errorMessage = err.message;
			}
			
			setError(errorMessage);
			return { success: false, message: errorMessage };
		} finally {
			setLoading(false);
		}
	};

	// Logout function
	const logout = async () => {
		try {
			if (token) {
				await authAPI.logout();
			}
		} catch (err) {
			console.error('Logout error:', err);
		} finally {
			localStorage.removeItem('authToken');
			localStorage.removeItem('user');
			setToken(null);
			setUser(null);
		}
	};

	// Update user profile
	const updateProfile = async (profileData) => {
		try {
			setError(null);
			const response = await authAPI.updateProfile(profileData);
			if (response.status === true || response.success === true) {
				const updatedUser = { ...user, ...profileData };
				setUser(updatedUser);
				localStorage.setItem('user', JSON.stringify(updatedUser));
				return { success: true, message: response.message };
			} else {
				throw new Error(response.message || 'Profile update failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Profile update failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Change password
	const changePassword = async (currentPassword, newPassword) => {
		try {
			setError(null);
			const response = await authAPI.changePassword(currentPassword, newPassword);
			if (response.status === true || response.success === true) {
				return { success: true, message: response.message };
			} else {
				throw new Error(response.message || 'Password change failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Password change failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Forgot password
	const forgotPassword = async (login) => {
		try {
			setError(null);
			const response = await authAPI.forgotPassword(login);
			if (response.success) {
				return { success: true, message: response.message };
			} else {
				throw new Error(response.message || 'Password reset request failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Password reset request failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Reset password
	const resetPassword = async (login, token, password, password_confirmation) => {
		try {
			setError(null);
			const response = await authAPI.resetPassword(login, token, password, password_confirmation);
			if (response.success) {
				return { success: true, message: response.message };
			} else {
				throw new Error(response.message || 'Password reset failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Password reset failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Upload avatar
	const uploadAvatar = async (file) => {
		try {
			setError(null);
			const response = await authAPI.uploadAvatar(file);
			if (response.success) {
				// Reload user profile to get updated avatar
				const profile = await authAPI.getProfile();
				const profileData = profile?.data || profile?.user || profile || null;
				if (profileData) {
					setUser(profileData);
					localStorage.setItem('user', JSON.stringify(profileData));
				}
				return { success: true, message: response.message || 'Avatar uploaded successfully' };
			} else {
				throw new Error(response.message || 'Avatar upload failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Avatar upload failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Verify email
	const verifyEmail = async (token) => {
		try {
			setError(null);
			const response = await authAPI.verifyEmail(token);
			if (response.success) {
				if (user) {
					const updatedUser = { ...user, emailVerified: true };
					setUser(updatedUser);
					localStorage.setItem('user', JSON.stringify(updatedUser));
				}
				return { success: true, message: response.message };
			} else {
				throw new Error(response.message || 'Email verification failed');
			}
		} catch (err) {
			const errorMessage = err.response?.data?.message || err.message || 'Email verification failed';
			setError(errorMessage);
			return { success: false, message: errorMessage };
		}
	};

	// Clear error
	const clearError = () => {
		setError(null);
	};

	// Force clear error (useful for navigation)
	const forceClearError = () => {
		setError(null);
	};

	const value = {
		user,
		token,
		loading,
		error,
		isAuthenticated: !!token && !!user,
		login,
		register,
		verifyEmailOrPhone,
		resendVerification,
		updateFarmDetails,
		updateExpertDetails,
		getProduce,
		storeFarmProduce,
		logout,
		updateProfile,
		changePassword,
		forgotPassword,
		resetPassword,
		verifyEmail,
		uploadAvatar,
		clearError,
		forceClearError,
	};

	return (
		<AuthContext.Provider value={value}>
			{children}
		</AuthContext.Provider>
	);
};
